import GatewayContainer from '../config/GatewayContainer';
import GatewayNames from '../constant/GatewayNames';
import { GroupClient } from '../client/group/GroupClient';
import { Group } from '../domain/rbac/RbacDomains';
import MessageUtil from '../util/MessageUtil';
import LanguageUtil from '../util/LanguageUtil';
import React from 'react';
import { CommonRoles, MemberPermission } from '../constant/Constants';
import { query } from '@trlogic/client-js';
import { dispatch } from '../redux/store';
import { openBulkActionDialogFromDependency } from '../redux/actions/BulkActionActions';
import { isStringValid } from '../util/Generator';
import Query = query.Query;

class GroupService {
  private groupClient: GroupClient = GatewayContainer.getInstance().get(GatewayNames.GROUP_CLIENT);

  public saveGroup = async (groupDto: SaveGroupDto): Promise<boolean | undefined> => {
    const { groupName } = groupDto;
    try {
      const groupIsValid = this.isGroupDtoValid(groupDto);
      if (!groupIsValid) return false;

      const exist = await this.isExist(groupName);
      if (exist) {
        MessageUtil.alreadyExist(LanguageUtil.get({ id: 'messages.warning.groupAlready', defaultMessage: 'Group already exist!' }));
        return false;
      }

      const group: Group = this.prepareGroup(groupDto);
      await this.groupClient.save(group.name, group);

      MessageUtil.success({
        summary: <div>{LanguageUtil.get({ id: 'messages.success.group', defaultMessage: 'Group' })}</div>,
        detail: <div>{LanguageUtil.get({ id: 'messages.created', defaultMessage: 'created.' })}</div>,
      });

      return true;
    } catch (e) {
      MessageUtil.error({
        summary: LanguageUtil.get({ id: 'messages.error.creatingGroup', defaultMessage: 'Error when creating group' }),
      });
    }
  };

  public updateGroup = async (groupDto: SaveGroupDto): Promise<boolean | undefined> => {
    const groupIsValid = this.isGroupDtoValid(groupDto);
    if (!groupIsValid) return false;

    try {
      const group = this.prepareGroup(groupDto);
      await this.groupClient.save(group.name, group);

      MessageUtil.success({
        summary: <div>{LanguageUtil.get({ id: 'messages.success.group', defaultMessage: 'Group' })}</div>,
        detail: <div>{LanguageUtil.get({ id: 'messages.updated', defaultMessage: 'updated.' })}</div>,
      });
      return true;
    } catch (e) {
      MessageUtil.error({
        summary: LanguageUtil.get({ id: 'messages.error.creatingGroup', defaultMessage: 'Error when creating group' }),
      });
      return false;
    }
  };

  public deleteGroup = async (id: string) => {
    try {
      await this.groupClient.remove(id);
      MessageUtil.success({
        summary: LanguageUtil.get({ id: 'messages.success.group', defaultMessage: 'Group' }),
        detail: (
          <div>
            <b>{id}</b> {LanguageUtil.get({ id: 'messages.removed', defaultMessage: 'deleted' })}
          </div>
        ),
      });
    } catch (e: any) {
      const dependencies: any = e.response.data;
      if (dependencies) {
        dispatch(openBulkActionDialogFromDependency(dependencies, 'Group' as any));
        return dependencies;
      }
      MessageUtil.success({
        summary: LanguageUtil.get({ id: 'messages.success.group', defaultMessage: 'Group' }),
        detail: (
          <div>
            <b>{id}</b> {LanguageUtil.get({ id: 'messages.error.cannotDeleted', defaultMessage: 'can not delete' })}
          </div>
        ),
      });
    }
  };

  public getCount = (query: Query) => {
    return this.groupClient.getCount(query);
  };

  private prepareGroup(groupDto: SaveGroupDto): Group {
    const { groupName, selectedRole, permission, selectedRoles, groupDescription, selectedUsers, createdAt, updatedAt, enabled } = groupDto;
    return {
      id: groupName,
      name: groupName,
      description: groupDescription || '',
      role: selectedRole,
      permission: selectedRole == 'Admin' || selectedRole == 'Root' ? 3 : permission!,
      roles: selectedRoles,
      users: selectedUsers,
      enabled: enabled ?? true,
      createdAt: createdAt,
      updatedAt: updatedAt,
    };
  }

  private isGroupDtoValid(saveGroupDto: SaveGroupDto): boolean {
    if (!(saveGroupDto.groupName || saveGroupDto.groupName == '')) {
      MessageUtil.warning({ summary: 'Name can not be empty' });
      return false;
    } else if (!isStringValid(saveGroupDto.groupName)) {
      MessageUtil.error({
        summary: LanguageUtil.get({ id: 'messages.warning.groupNameSpecialCharacters', defaultMessage: 'Group name can not have any special character' }),
      });
      return false;
    }
    return true;
  }

  private async isExist(groupName: string): Promise<boolean> {
    return await this.groupClient.exist(groupName);
  }
}

export interface SaveGroupDto {
  groupName: string;
  groupDescription: string;
  selectedRole: CommonRoles;
  selectedRoles: string[];
  selectedUsers: string[];
  permission: MemberPermission;
  enabled?: boolean;
  createdAt: number | undefined;
  updatedAt: number | undefined;
}

export default GroupService;
